import { Injectable, inject, signal, computed } from '@angular/core';
import { UserScopedStorageService } from './user-scoped-storage.service';

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'smartcapacity.theme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly userStorage = inject(UserScopedStorageService);
  private readonly _currentTheme = signal<Theme>(this.getSystemTheme());

  readonly currentTheme = computed(() => this._currentTheme());

  /**
   * Initialize theme from localStorage or system preference.
   * Returns the detected/selected theme.
   */
  initializeTheme(): Theme {
    // Check localStorage first (user preference wins)
    const storedTheme = this.getStoredTheme();
    if (storedTheme) {
      this._currentTheme.set(storedTheme);
      return storedTheme;
    }

    // Use system preference as default
    const systemTheme = this.getSystemTheme();
    this._currentTheme.set(systemTheme);

    return systemTheme;
  }

  /**
   * Toggle between light and dark themes.
   */
  toggleTheme(): void {
    const newTheme: Theme = this._currentTheme() === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Set the current theme and persist to localStorage.
   */
  setTheme(theme: Theme): void {
    this._currentTheme.set(theme);
    this.saveThemeToStorage(theme);
    this.applyThemeToDocument(theme);
  }

  /**
   * Get the current theme.
   */
  getCurrentTheme(): Theme {
    return this._currentTheme();
  }

  /**
   * Apply theme to document root element.
   */
  applyThemeToDocument(theme: Theme): void {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset['theme'] = theme;
    }
  }

  /**
   * Detect system theme preference.
   */
  private getSystemTheme(): Theme {
    if (globalThis.window !== undefined && globalThis.matchMedia) {
      return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light'; // Default fallback
  }

  /**
   * Get theme from localStorage.
   */
  private getStoredTheme(): Theme | null {
    const stored = this.userStorage.get(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  }

  private saveThemeToStorage(theme: Theme): void {
    this.userStorage.set(THEME_STORAGE_KEY, theme);
  }
}
